import {unwrapResult} from '@reduxjs/toolkit';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {
  Col,
  UiBox,
  UiButton,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiSimpleGrid,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {BIRTH_CONTROL_REQUEST_KEYS, METHOD_KEYS} from '~/constants/medical-questionnaires';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer, setUserQuestionnaireId} from '~/redux/questionnaires/slice';
import {addQuestionnaire} from '~/redux/questionnaires/thunk';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {getCurrentUserFromStorage} from '~/services/auth';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

const Questionnaire11Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const {accessToken: savedToken} = getCurrentUserFromStorage();
  const {addMessage, addValidationMessage} = useNotification(dispatch);

  const {loading} = useRequestState(addQuestionnaire);

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.OTHER_MEDICAL_HISTORY]?.data,
  );

  const bestStatementAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[BIRTH_CONTROL_REQUEST_KEYS.STATEMENT_BEST_APPLIES]?.data?.selectedOption,
  );

  const medicalQuestionnaireAnswer = useSelector((state: RootState) => state.questionnaires?.collectData);

  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(i => i.key === BIRTH_CONTROL_REQUEST_KEYS.OTHER_MEDICAL_HISTORY);

  const rules = {
    medicationsTaking: (attributes: any) => {
      return {
        presence: {allowEmpty: false, message: `^This field can't be blank`},
        length: {
          minimum: 5,
          tooShort: '^This field is too short (minimum is %{count} characters)',
          tokenizer: function (value: any) {
            return trimSpaces(value);
          },
        },
      };
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
    },
    {rules},
  );

  const onClickOption = (answer: {value: string | number; label: string; relations?: Relation[]}) => {
    const {value} = answer;
    setInputs({selectedOption: value, medicationsTaking: ''});
  };

  const onClickSubmit = async () => {
    if (inputs.selectedOption === 'YES') {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    const questionnaireAnswer = {
      key: BIRTH_CONTROL_REQUEST_KEYS.OTHER_MEDICAL_HISTORY,
      index: questionnaire?.index || '',
      data: {
        selectedOption: inputs.selectedOption || '',
        medicationsTaking: inputs.medicationsTaking.trim() || '',
      },
    };
    dispatch(setQuestionnaireAnswer(questionnaireAnswer));
    if (bestStatementAnswer === 1) {
      dispatch(setNextQuestionnaire({value: BIRTH_CONTROL_REQUEST_KEYS.CHOOSE_MEDICATION}));
    } else {
      const resAction = await dispatch(
        addQuestionnaire({
          data: {
            data: {
              ...medicalQuestionnaireAnswer,
              [BIRTH_CONTROL_REQUEST_KEYS.OTHER_MEDICAL_HISTORY]: {...questionnaireAnswer},
            },
            wpUserId: wpAuthUser?.wpUserId,
            methodKey: METHOD_KEYS.BIRTH_CONTROL_REQUEST,
          },
        }),
      );
      if (requestOK(resAction, addQuestionnaire)) {
        const result = unwrapResult(resAction);
        if (result?.booking) {
          const type = result?.slotTime;
          dispatch(setUserQuestionnaireId({id: result?.medical?.id}));
          history.push(`/appointment?type=${type}&methodKey=${METHOD_KEYS.BIRTH_CONTROL_REQUEST}&token=${savedToken}`);
        }
      }
    }
  };

  const renderTellMore = () => {
    if (inputs.selectedOption === 'YES') {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
          <UiText fontSize="md" fontWeight="bold">
            Tell us more
          </UiText>
          <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
            <UiTextarea
              mt={spacing.small}
              bg="white"
              borderRadius={spacing.tiny}
              placeholder="Provide as much detail as possible"
              value={inputs.medicationsTaking}
              onChange={({target}) => setInputs({medicationsTaking: target.value})}
              minH="200px"
            />
            {validation?.errors?.medicationsTaking && (
              <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
        </UiBox>
      );
    } else {
      return (
        <UiBox mt={spacing.small} className="animate__animated animate__fadeInLeft" w="100%">
          {questionnaire?.descriptions?.default?.map(val => (
            <UiText key={val} mt={spacing.small} textColor="liteOuter">
              {val}
            </UiText>
          ))}
        </UiBox>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      disableNext={!inputs.selectedOption}
      handleOnNext={onClickSubmit}
      loading={loading}
      checkoutStep={questionnaire?.checkoutStep}
    >
      <Col alignItems="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiBox mt={spacing.small} w="100%">
          <UiSimpleGrid columns={2} spacing={5}>
            {questionnaire?.options?.map(value => (
              <ScrollToBottomButton
                key={`${value.label}-${value.value}`}
                title={value.label}
                variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
                onClick={() => onClickOption(value)}
              />
            ))}
          </UiSimpleGrid>
        </UiBox>
        {renderTellMore()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire11Screen;
